import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const STATUS_COLOR = {
  PENDING:   { bg: 'rgba(234,179,8,0.12)',  fg: '#FACC15' },
  QUALIFIED: { bg: 'rgba(56,189,248,0.12)', fg: '#38BDF8' },
  PAID_OUT:  { bg: 'rgba(16,185,129,0.12)', fg: '#34D399' },
  EXPIRED:   { bg: 'rgba(148,163,184,0.12)',fg: '#94A3B8' },
  VOIDED:    { bg: 'rgba(239,68,68,0.12)',  fg: '#F87171' },
};

const fmtCents = (cents) => `€${(cents / 100).toFixed(2)}`;

export default function ReferralsTab({ token, isOwner }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyOk, setCopyOk] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null); // { kind: 'ok' | 'err', text }

  const load = useCallback(async () => {
    setError(null);
    try {
      const [meRes, listRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/referrals/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/v1/referrals?page=0&size=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!meRes.ok || !listRes.ok) throw new Error('load');
      setSummary(await meRes.json());
      const list = await listRes.json();
      setReferrals(Array.isArray(list.content) ? list.content : []);
    } catch {
      setError(t('referrals.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  const shareUrl = summary
    ? `${window.location.origin}/admin/signup?ref=${summary.code}`
    : '';

  const handleCopy = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  }, [shareUrl]);

  const handleSendInvite = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = inviteEmail.trim();
    // Same shape as the backend @Email validator — keeps client-side UX tight
    // without reinventing the Bean Validation rule.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setInviteMsg({ kind: 'err', text: t('referrals.invite.invalidEmail') });
      return;
    }
    setSending(true);
    setInviteMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/referrals/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) throw new Error('send');
      setInviteEmail('');
      setInviteMsg({ kind: 'ok', text: t('referrals.invite.sent') });
    } catch {
      setInviteMsg({ kind: 'err', text: t('referrals.invite.sendError') });
    } finally {
      setSending(false);
    }
  }, [inviteEmail, token, t]);

  const handleRotate = useCallback(async () => {
    if (!window.confirm(t('referrals.rotateConfirm'))) return;
    setRotating(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/referrals/code/rotate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('rotate');
      const next = await res.json();
      setSummary(next);
    } catch {
      setError(t('referrals.rotateError'));
    } finally {
      setRotating(false);
    }
  }, [token, t]);

  if (loading) return <div style={s.card}><p style={s.muted}>{t('common.loading')}…</p></div>;
  if (!summary) return <div style={s.card}><p style={s.error}>{error || t('referrals.loadError')}</p></div>;

  return (
    <>
      <div style={s.card}>
        <h2 style={s.title}>{t('referrals.title')}</h2>
        <p style={s.subtitle}>{t('referrals.subtitle')}</p>

        <div style={s.statsRow}>
          <Stat label={t('referrals.statPending')}  value={summary.pendingCount} />
          <Stat label={t('referrals.statPaidOut')} value={summary.paidOutCount} />
          <Stat label={t('referrals.statCredit')}  value={fmtCents(summary.availableCreditCents)} />
        </div>

        {summary.freeMonthsPending > 0 && (
          <div style={s.refereeBanner}>
            {t('referrals.freeMonthsBanner', { count: summary.freeMonthsPending })}
          </div>
        )}

        <label style={s.label}>{t('referrals.shareLink')}</label>
        <div style={s.urlRow}>
          <input style={s.url} value={shareUrl} readOnly />
          <button style={s.btn} onClick={handleCopy}>
            {copyOk ? `✓ ${t('common.copied')}` : t('referrals.copy')}
          </button>
        </div>

        <p style={{ ...s.muted, fontSize: '0.78rem', marginTop: '0.75rem' }}>
          {t('referrals.codeLabel')}: <code style={s.code}>{summary.code}</code>
          {isOwner && (
            <>
              {' · '}
              <button style={s.linkBtn} onClick={handleRotate} disabled={rotating}>
                {rotating ? t('referrals.rotating') : t('referrals.rotate')}
              </button>
            </>
          )}
        </p>

        <div style={s.inviteBlock}>
          <label style={s.label}>{t('referrals.invite.label')}</label>
          <p style={{ ...s.muted, fontSize: '0.8rem', marginTop: 0, marginBottom: '0.5rem' }}>
            {t('referrals.invite.hint')}
          </p>
          <form style={s.urlRow} onSubmit={handleSendInvite}>
            <input
              type="email"
              style={s.url}
              placeholder={t('referrals.invite.placeholder')}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={sending}
            />
            <button style={s.btn} type="submit" disabled={sending || !inviteEmail.trim()}>
              {sending ? t('referrals.invite.sending') : t('referrals.invite.send')}
            </button>
          </form>
          {inviteMsg && (
            <p style={{
              ...(inviteMsg.kind === 'ok' ? s.inviteOk : s.error),
              fontSize: '0.8rem',
              marginTop: '0.5rem',
            }}>
              {inviteMsg.text}
            </p>
          )}
        </div>
      </div>

      <div style={s.card}>
        <h3 style={s.subTitle}>{t('referrals.listTitle')}</h3>
        {referrals.length === 0 ? (
          <p style={s.muted}>{t('referrals.empty')}</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>{t('referrals.colReferee')}</th>
                <th style={s.th}>{t('referrals.colStatus')}</th>
                <th style={s.th}>{t('referrals.colReward')}</th>
                <th style={s.th}>{t('referrals.colJoined')}</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r) => (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>{r.refereeEmailRedacted}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: STATUS_COLOR[r.status]?.bg || s.badge.background,
                      color: STATUS_COLOR[r.status]?.fg || s.badge.color,
                    }}>{r.status}</span>
                  </td>
                  <td style={s.td}>{r.rewardAmountCents != null ? fmtCents(r.rewardAmountCents) : '—'}</td>
                  <td style={s.td}>{new Date(r.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div style={s.stat}>
      <div style={s.statValue}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const s = {
  card: {
    background: '#1E293B', borderRadius: 12, padding: '1.5rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', marginBottom: '1rem',
  },
  title: { margin: '0 0 0.25rem', fontWeight: 700, fontSize: '1.05rem', color: '#F8FAFC' },
  subTitle: { margin: '0 0 1rem', fontWeight: 700, fontSize: '0.95rem', color: '#F8FAFC' },
  subtitle: { margin: '0 0 1rem', fontSize: '0.85rem', color: '#94A3B8' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#94A3B8', marginBottom: 6 },
  statsRow: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  stat: {
    flex: '1 1 100px', background: '#0F172A', borderRadius: 8,
    padding: '0.75rem 1rem', border: '1px solid #334155',
  },
  statValue: { fontSize: '1.4rem', fontWeight: 800, color: '#F8FAFC' },
  statLabel: { fontSize: '0.75rem', color: '#94A3B8', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.04em' },
  refereeBanner: {
    background: 'rgba(16,185,129,0.12)', color: '#34D399',
    padding: '0.6rem 0.75rem', borderRadius: 8, fontSize: '0.85rem',
    marginBottom: '1rem', border: '1px solid rgba(16,185,129,0.3)',
  },
  urlRow: { display: 'flex', gap: '0.5rem' },
  url: {
    flex: 1, padding: '0.55rem 0.75rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.85rem', background: '#0F172A', color: '#F8FAFC', fontFamily: 'monospace',
  },
  btn: {
    padding: '0 1rem', borderRadius: 6, border: 'none',
    background: '#7C3AED', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  code: { background: '#0F172A', padding: '2px 6px', borderRadius: 4, color: '#A78BFA', fontFamily: 'monospace' },
  linkBtn: {
    background: 'transparent', border: 'none', color: '#7C3AED', cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: 600, padding: 0,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '0.5rem 0.6rem', fontSize: '0.75rem',
    color: '#94A3B8', borderBottom: '1px solid #334155', textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  td: { padding: '0.55rem 0.6rem', fontSize: '0.85rem', color: '#CBD5E1' },
  tr: { borderBottom: '1px solid #1E293B' },
  badge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: '0.7rem', fontWeight: 700, background: '#334155', color: '#94A3B8',
  },
  muted: { color: '#94A3B8', fontSize: '0.85rem', margin: 0 },
  error: { color: '#F87171', fontSize: '0.85rem', margin: 0 },
  inviteBlock: { marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #334155' },
  inviteOk: { color: '#34D399', margin: 0 },
};
